import { Mountain, Ticket, ArrowRight, Radio } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RaceCard } from '@/components/races/RaceCard';
import { isRaceLive } from '@/lib/live';
import type { HomeOpenRacesQuery } from '@/gql/graphql';

type RaceItem = NonNullable<NonNullable<HomeOpenRacesQuery['listRaces']>['items']>[number];

interface OpenRacesProps {
    races: RaceItem[];
    totalCount: number;
}

function RaceCardWithBadges({ race }: { race: RaceItem }) {
    const { t } = useTranslation('home');
    const live = isRaceLive(race);

    return (
        <RaceCard
            key={race.id}
            id={race.id!}
            name={race.name ?? ''}
            raceFormat={race.raceFormat}
            bettingDeadline={race.bettingDeadline}
            runnerCount={race.runners?.length ?? 0}
            bettorCount={race.bettorCount ?? 0}
            isOfficial={race.isOfficial}
            isLive={live}
            showBetNow={!race.hasCurrentUserBet && !live}
            badges={
                race.hasCurrentUserBet ? (
                    <span className="ml-2 flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 flex items-center gap-1">
                        <Ticket size={10} />
                        {t('openRaces.betPlaced')}
                    </span>
                ) : undefined
            }
        />
    );
}

export function OpenRaces({ races, totalCount }: OpenRacesProps) {
    const { t } = useTranslation('home');

    const liveRaces = races.filter(r => isRaceLive(r));
    const otherRaces = races.filter(r => !isRaceLive(r));

    if (races.length === 0) {
        return (
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Mountain size={20} className="text-emerald-500" />
                    {t('sections.openRaces')}
                </h2>
                <div className="py-10 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                    <Mountain size={32} className="mx-auto mb-2 text-slate-200" />
                    <p className="text-slate-400 text-sm font-medium">{t('openRaces.empty')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Live races section */}
            {liveRaces.length > 0 && (
                <div className="space-y-3">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <span className="relative flex h-5 w-5 items-center justify-center">
                            <Radio size={16} className="text-rose-500" />
                            <span className="absolute inset-0 rounded-full bg-rose-400 animate-ping opacity-30" />
                        </span>
                        {t('sections.liveNow')}
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                        {liveRaces.map((race) => (
                            <RaceCardWithBadges key={race.id} race={race} />
                        ))}
                    </div>
                </div>
            )}

            {/* Other open races */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Mountain size={20} className="text-emerald-500" />
                        {t('sections.openRaces')}
                        <span className="text-sm font-normal text-slate-400">({totalCount})</span>
                    </h2>
                    <Link
                        to="/races"
                        className="text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                    >
                        {t('seeAll')}
                        <ArrowRight size={14} />
                    </Link>
                </div>

                {otherRaces.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2">
                        {otherRaces.map((race) => (
                            <RaceCardWithBadges key={race.id} race={race} />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-slate-400 text-center py-4">{t('openRaces.allLive')}</p>
                )}
            </div>
        </div>
    );
}
